import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { AdminUrlBuilder } from '../../../../../../shared/admin/api/admin-url-builder';
import { BreadcrumbBuilder } from '../../../../../../shared/ui/breadcrumb/breadcrumb-builder';
import { IDropdownSearcherOptions } from '../../../../../../shared/ui/component/dropdown-searcher/dropdown-searcher.interfaces';
import { AdminPrintCreditListDatatableComponent } from './datatable/print-credit-list-datatable.component';
import { CampaignOption } from '../../types';
import { AdminPrintCreditFormModal } from '../../modals/print-credit-form/print-credit-form.component';

@Component({
    templateUrl: './print-credit-list.component.html',
    styleUrls: ['./print-credit-list.component.scss'],
})
export class AdminPrintCreditListComponent implements OnInit, OnDestroy {
    modalRef: BsModalRef;
    @ViewChild(AdminPrintCreditListDatatableComponent, { static: false })
    datatableComponent: AdminPrintCreditListDatatableComponent;

    dropdownSearchOptions: IDropdownSearcherOptions = {
        minSearchPhraseLength: 0,
        dropdownMaxHeight: '420px',
        placeholder: 'Select campaign',
    };

    addPrintCreditEventSubscription: Subscription;
    private originalCampaignsOptionsSubject: BehaviorSubject<CampaignOption[]> = new BehaviorSubject<CampaignOption[]>([
        { value: 0, label: 'All campaigns', uuid: '', id: 0 },
    ]);
    campaignsOptionsSubject: BehaviorSubject<CampaignOption[]> = new BehaviorSubject<CampaignOption[]>([]);
    campaignsOptions$: Observable<CampaignOption[]> = this.campaignsOptionsSubject.asObservable();

    selectedCampaign: number = null;
    hasOriginalCampaignOption: boolean = true;

    constructor(
        private modalService: BsModalService,
        private httpClient: HttpClient,
        private urlBuilder: AdminUrlBuilder,
        private breadcrumbBuilder: BreadcrumbBuilder
    ) {}

    ngOnInit(): void {
        this.breadcrumbBuilder.add({
            label: 'Print credits ',
            url: '/list',
        });

        this.httpClient.get<any>(`${this.buildUrl()}/all`).subscribe((response) => {
            const fetchedCampaigns = response.campaigns.map(
                (campaign: { id: string; name: string; uuid: string; languageDefault: string }) => ({
                    value: campaign.id,
                    label: campaign.name,
                    uuid: campaign.uuid,
                    id: campaign.id,
                    language: campaign.languageDefault,
                })
            );
            fetchedCampaigns.sort((a, b) => a.label.localeCompare(b.label));
            this.hasOriginalCampaignOption = fetchedCampaigns.length === 0;

            this.originalCampaignsOptionsSubject.next([
                ...this.originalCampaignsOptionsSubject.getValue(),
                ...fetchedCampaigns,
            ]);

            this.campaignsOptionsSubject.next(this.originalCampaignsOptionsSubject.getValue());
        });
    }

    ngOnDestroy(): void {
        if (this.addPrintCreditEventSubscription) this.addPrintCreditEventSubscription.unsubscribe();
    }

    private buildUrl() {
        let url = '/campaign';
        if (this.urlBuilder) {
            url = this.urlBuilder.buildUrl(url);
        }

        return url;
    }

    reloadDatatableAfterAdding(): void {
        this.datatableComponent.load();
    }

    handleSearch(phrase: string) {
        const originalOptions = this.originalCampaignsOptionsSubject.getValue();

        if (!phrase) {
            this.campaignsOptionsSubject.next(originalOptions);
            return;
        }

        const filteredOptions = originalOptions.filter((option) => {
            return option.label.toLowerCase().includes(phrase.toLowerCase()) || '';
        });

        this.campaignsOptionsSubject.next(filteredOptions);
    }

    handleChange(selectedItem: CampaignOption) {
        this.selectedCampaign = selectedItem.value;
    }

    addPrintCredit() {
        const originalCampaignsOptions = this.originalCampaignsOptionsSubject.getValue();
        const filteredCampaignsOptions = originalCampaignsOptions.slice(1); // remove 'All campaigns'

        this.modalRef = this.modalService.show(AdminPrintCreditFormModal, {
            initialState: {
                campaignsOptions: filteredCampaignsOptions,
                dropdownSearchOptions: this.dropdownSearchOptions,
            },
        });

        this.addPrintCreditEventSubscription = this.modalRef.content.onPrintCreditSaved.subscribe((result: number) => {
            this.reloadDatatableAfterAdding();
        });
    }
}
